import React from "react";
import { useTheme } from "@mui/styles";
import { Typography, Box } from "@mui/material";
import WantToStartButton from "../Buttons/WantToStartButton";
import { useLocation } from "react-router-dom";
import useTranslation from "../../hooks/useTranslation";

const useStyles = (theme) => ({
  container: {
    display: "flex",
    flexDirection: { xs: "column" },
    alignItems: { xs: "center" },
    backgroundImage:
      "linear-gradient(to bottom, rgba(0, 237, 213, 0.07) 0%, rgba(231, 240, 252, 0) 100%);",
    backgroundPosition: { xs: "center" },
    backgroundRepeat: "no-repeat",
    backgroundSize: "100% 250px",
    width: { xs: "100%" },
  },
  child: {
    display: "flex",
  },
  firstCard: {
    display: "flex",
    flexDirection: { xs: "column" },
    alignItems: { xs: "center" },
    background: "linear-gradient(to bottom, #23213f 0%, #03183d 100%)",
    height: { md: "300px", lg: "360px", xl: "454px" },
    width: { md: "270px", lg: "336px", xl: "418px" },
    borderRadius: "40px",
    mt: { md: "60px", lg: "80px", xl: "101px" },
    mr: { md: "20px", lg: "30px", xl: "50px" },
  },
  firstTitle: {
    color: theme.palette.green.main,
    fontSize: { md: "22px", lg: "26px", xl: "30px" },
    lineHeight: { md: "36px" },
    width: { md: "201px", lg: "266px", xl: "335px" },
    fontFamily: theme.typography.montserrat.bold,
    textAlign: { xs: "center" },
    mt: { md: "12px", lg: "30px", xl: "37.4px" },
  },
  firstSubtitle: {
    color: theme.palette.blue.light,
    fontFamily: theme.typography.anaHeim.regular,
    fontSize: { md: "17px", lg: "20px", xl: "24px" },
    lineHeight: { md: "26px", lg: "28px", xl: "36px" },
    maxWidth: { md: "200px", lg: "229px", xl: "287px" },
    height: { xl: "108px" },
    mt: { md: "20.4px", lg: "27.4px", xl: "34px" },
    textAlign: { xs: "center" },
  },
  icon: {
    height: { md: "50px", lg: "60px", xl: "72.6px" },
    width: { md: "50px", lg: "52px", xl: "65px" },
    color: theme.palette.green.main,
    mt: { md: "15px", lg: "30px", xl: "55px" },
  },
  secondCard: {
    display: "flex",
    flexDirection: { xs: "column" },
    alignItems: { xs: "center" },
    background: theme.palette.white.main,
    height: { md: "300px", lg: "364px", xl: "454px" },
    width: { md: "270px", lg: "336px", xl: "418px" },
    borderRadius: "40px",
    mt: { md: "97px", lg: "130px", xl: "162px" },
    boxShadow: "0 10px 50px 0 rgba(0, 0, 0, 0.05)",
    mr: { md: "20px", lg: "30px", xl: "50px" },
  },
  secondIcon: {
    height: { md: "50px", lg: "60px", xl: "72.6px" },
    width: { md: "50px", lg: "52px", xl: "65px" },
    color: theme.palette.green.main,
    mt: { md: "15px", lg: "30px", xl: "55px" },
  },
  secondTitle: {
    color: theme.palette.blue.main,
    fontSize: { md: "22px", lg: "26px", xl: "30px" },
    lineHeight: { md: "36px" },
    maxWidth: { lg: "164px", xl: "206px" },
    height: { xl: "72px" },
    fontFamily: theme.typography.montserrat.bold,
    textAlign: { xs: "center" },
    mt: { md: "12px", lg: "32px", xl: "43px" },
  },
  secondSubtitle: {
    color: theme.palette.blue.main,
    fontFamily: theme.typography.anaHeim.regular,
    fontSize: { md: "17px", lg: "20px", xl: "24px" },
    lineHeight: { md: "26px", lg: "28px", xl: "36px" },
    maxWidth: { md: "225px", lg: "288px", xl: "359px" },
    height: { xl: "108px" },
    mt: { md: "12px", lg: "16px", xl: "19px" },
    textAlign: { xs: "center" },
  },
  thirdCard: {
    display: "flex",
    flexDirection: { xs: "column" },
    alignItems: { xs: "center" },
    background: theme.palette.white.main,
    height: { md: "300px", lg: "363px", xl: "454px" },
    width: { md: "270px", lg: "336px", xl: "418px" },
    borderRadius: "40px",
    mt: { md: "99px", lg: "132px", xl: "162px" },
    boxShadow: "0 10px 50px 0 rgba(0, 0, 0, 0.05)",
  },
  thirdIcon: {
    height: { md: "50px", lg: "60px", xl: "72.6px" },
    width: { md: "50px", lg: "52px", xl: "65px" },
    color: theme.palette.green.main,
    mt: { md: "15px", lg: "30px", xl: "55px" },
  },
  thirdTitle: {
    color: theme.palette.blue.main,
    fontSize: { md: "22px", lg: "26px", xl: "30px" },
    lineHeight: { md: "36px" },
    width: { md: "201px", lg: "268px", xl: "337px" },
    height: { xl: "72px" },
    fontFamily: theme.typography.montserrat.bold,
    textAlign: { xs: "center" },
    mt: { md: "18px", lg: "24px", xl: "30px" },
  },
  thirdSubtitle: {
    color: theme.palette.blue.main,
    fontFamily: theme.typography.anaHeim.regular,
    fontSize: { md: "17px", lg: "20px", xl: "24px" },
    lineHeight: { md: "26px", lg: "28px", xl: "36px" },
    width: { md: "240px", lg: "300px", xl: "375px" },
    height: { xl: "108px" },
    mt: { md: "12px", lg: "16px", xl: "19px" },
    textAlign: { xs: "center" },
  },
});

const StagesDesktop = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { pathname } = useLocation();
  const { exercises, codingChallenge, personalInterview } = useTranslation(
    pathname,
    "StagesDesktop"
  );

  return (
    <Box sx={classes.container}>
      <Box sx={classes.child}>
        <Box sx={classes.secondCard}>
          <img
            src={exercises.img.src}
            alt={exercises.img.alt}
            style={{ height: "57px", margin: "3rem" }}
          />
          <Typography sx={classes.secondTitle}>{exercises.title}</Typography>
          <Typography sx={classes.secondSubtitle}>
            {exercises.subtitle}
          </Typography>
        </Box>

        <Box sx={classes.firstCard}>
          <img
            src={codingChallenge.img.src}
            alt={codingChallenge.img.alt}
            style={{ height: "57px", margin: "3rem" }}
          />
          <Typography sx={classes.firstTitle}>
            {codingChallenge.title}
          </Typography>
          <Typography sx={classes.firstSubtitle}>
            {codingChallenge.subtitle}
          </Typography>
        </Box>

        <Box sx={classes.thirdCard}>
          <img
            src={personalInterview.img.src}
            alt={personalInterview.img.alt}
            style={{ height: "57px", margin: "3rem" }}
          />
          <Typography sx={classes.thirdTitle}>
            {personalInterview.title}
          </Typography>
          <Typography sx={classes.thirdSubtitle}>
            {personalInterview.subtitle}
          </Typography>
        </Box>
      </Box>

      <Box>
        <WantToStartButton
          mainColor="#00edd5"
          secondaryColor="#04a695"
          iconColor="#0feed7"
          mt="61px"
        />
      </Box>
    </Box>
  );
};

export default StagesDesktop;
